/**
 * StatusCell Component
 *
 * Displays a StatusChip for risk or asset entities.
 * Uses existing status utilities for consistent label/variant mapping.
 */

import { StatusChip, type StatusChipVariant } from '@/components/legacy-ui/StatusChip';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';
import {
  assetStatusVariants,
  getVariantForStatus,
  vulnerabilityStatusVariants,
} from '@/utils/statusChip.util';

/**
 * Props for StatusCell component
 */
export interface StatusCellProps {
  /** Raw status code (e.g., 'OH' for risks, 'A' for assets) */
  status: string | undefined;
  /** Entity type to determine which variant map to use */
  entityType: 'asset' | 'risk' | undefined;
}

/**
 * Asset status code to label mapping
 */
const ASSET_STATUS_LABELS: Record<string, string> = {
  A: 'Active',
  D: 'Deleted',
  F: 'Frozen',
  P: 'Pending',
};

/**
 * Get StatusChip props (label and variant) based on status and entity type
 */
function getStatusChipProps(
  status: string | undefined,
  entityType: 'asset' | 'risk' | undefined
): { label: string; variant: StatusChipVariant } | null {
  if (!status || !entityType) return null;

  if (entityType === 'risk') {
    const { statusLabel } = getRiskStatusLabel(status);
    if (!statusLabel) return null;
    const variant = getVariantForStatus(
      statusLabel,
      vulnerabilityStatusVariants
    );
    return { label: statusLabel, variant };
  }

  if (entityType === 'asset') {
    const statusCode = status[0]; // First character is the status
    const label = ASSET_STATUS_LABELS[statusCode];
    if (!label) return null;
    const variant = getVariantForStatus(label, assetStatusVariants);
    return { label, variant };
  }

  return null;
}

/**
 * StatusCell displays a StatusChip based on entity status.
 *
 * Returns null for invalid status/entityType combinations.
 *
 * @example
 * ```tsx
 * <StatusCell status="OH" entityType="risk" />  // Shows "Open" chip
 * <StatusCell status="A" entityType="asset" />  // Shows "Active" chip
 * ```
 */
export function StatusCell({ status, entityType }: StatusCellProps) {
  const chipProps = getStatusChipProps(status, entityType);

  if (!chipProps) {
    return null;
  }

  return <StatusChip label={chipProps.label} variant={chipProps.variant} />;
}
